"use client"

import { router } from "@inertiajs/react"

import { Badge, BadgeWithDot } from "@/components/ui/badge"
import { Table, TableCard } from "@/components/ui/table"
import { formatDate } from "@/lib/utils/"
import { editAdminPlaylistPath } from "@/routes"

export interface PlaylistRow {
  id: number
  title: string
  creator: string
  published: boolean
  outro_count: number
  tracks_count: number
  updated_at: string
}

interface PlaylistsTableProps {
  rows: PlaylistRow[]
  "aria-label"?: string
  footer?: React.ReactNode
}

// Every playlist as an admin row: visibility, whether an outro is placed,
// and its size. The whole row opens the playlist's admin editor.
export function PlaylistsTable({
  rows,
  "aria-label": ariaLabel = "Playlists",
  footer,
}: PlaylistsTableProps) {
  return (
    <TableCard.Root>
      <Table aria-label={ariaLabel} className="bg-primary">
        <Table.Header>
          <Table.Head
            id="title"
            isRowHeader
            label="Playlist"
            className="w-full"
          />
          <Table.Head id="visibility" label="Visibility" />
          <Table.Head id="onwards" label="Onwards" />
          <Table.Head id="tracks" label="Tracks" className="text-right" />
          <Table.Head id="updated" label="Updated" />
        </Table.Header>
        <Table.Body items={rows}>
          {(row: PlaylistRow) => (
            <Table.Row
              id={row.id}
              className="cursor-pointer"
              onAction={() => router.visit(editAdminPlaylistPath(row.id))}
            >
              <Table.Cell>
                <div className="flex flex-col">
                  <span className="text-primary text-sm font-medium">
                    {row.title}
                  </span>
                  <span className="text-tertiary text-xs">{row.creator}</span>
                </div>
              </Table.Cell>
              <Table.Cell>
                <BadgeWithDot
                  color={row.published ? "success" : "gray"}
                  size="sm"
                  type="modern"
                >
                  {row.published ? "Public" : "Draft"}
                </BadgeWithDot>
              </Table.Cell>
              <Table.Cell>
                {row.outro_count > 0 ? (
                  <span className="text-tertiary text-sm tabular-nums">
                    {row.outro_count} placed
                  </span>
                ) : row.published ? (
                  <Badge color="warning" size="sm" type="pill-color">
                    No outro
                  </Badge>
                ) : (
                  <span className="text-quaternary text-sm">None</span>
                )}
              </Table.Cell>
              <Table.Cell className="text-tertiary text-right tabular-nums">
                {row.tracks_count}
              </Table.Cell>
              <Table.Cell className="text-tertiary whitespace-nowrap">
                {formatDate(row.updated_at)}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      {footer}
    </TableCard.Root>
  )
}
